import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  children?: React.ReactNode;
  noindex?: boolean;
  structuredData?: object[];
}

// US-focused default keywords for better geo-targeting
const DEFAULT_KEYWORDS = [
  // Primary product keywords
  "chatbot",
  "AI chatbot",
  "ai chat",
  "ai bot",
  "chatbot ai",
  "AI chatbot builder",
  "chatbot platform",
  "customer support chatbot",
  "chatbot customer service",
  "business chatbot",
  "conversational AI",
  "artificial intelligence chat",
  "best AI chatbot",

  // US-specific keywords
  "AI chatbot USA",
  "chatbot for US businesses",
  "American business automation",
  "customer service automation USA",
  "US chatbot software",
  
  // Industry-specific (US market)
  "small business chatbot",
  "ecommerce chatbot",
  "website chatbot",
  "live chat alternative",
  "customer support automation",
  
  // Feature keywords
  "no code chatbot",
  "easy chatbot builder",
  "24/7 customer support",
  "AI customer service",
  "automated customer support",
  "chatbot online",
  "AI chatbot online",
  "AI chat online",
  "chatbot live",
  "talk to AI",

  // Free-tier keywords
  "free AI chatbot",
  "AI chatbot free",
  "free chatbot",
  "chatbot free",
  "free AI chat",
  "chatbot free online",

  // GPT-related keywords
  "chatbot GPT",
  "GPT chatbot",
  "AI chat GPT",

  // Competitor alternatives
  "Intercom alternative",
  "Intercom chatbot alternative",
  "Drift alternative",
  "Drift chatbot alternative",
  "Zendesk chat alternative",
  "Zendesk chatbot alternative",
  "HubSpot chatbot alternative",
  "Ada chatbot alternative",
  "Dialogflow chatbot alternative",
  "affordable chatbot",
  
  // Brand
  "ChytBot",
];

export const SEO = ({
  title = "ChytBot - AI Chatbot Platform for US Businesses | 24/7 Customer Support",
  description = "Build AI-powered chatbots for your American business. Automate customer support, boost conversions, and save time. No coding required. Trusted by businesses across the United States.",
  keywords = DEFAULT_KEYWORDS,
  image = "https://chytbot.com/og-image.png",
  url = "https://chytbot.com",
  type = "website",
  noindex = false,
  structuredData: additionalStructuredData,
  children,
}: SEOProps) => {
  const siteTitle = "ChytBot";
  const fullTitle = title.includes(siteTitle) ? title : `${title} | ${siteTitle}`;
  
  // Ensure URL has trailing consistency
  const canonicalUrl = url.endsWith('/') ? url.slice(0, -1) : url;

  // Base structured data for the page
  const baseStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      name: fullTitle,
      description: description,
      url: canonicalUrl,
      isPartOf: {
        "@type": "WebSite",
        "@id": "https://chytbot.com#website",
        name: "ChytBot",
        url: "https://chytbot.com",
      },
      publisher: {
        "@type": "Organization",
        "@id": "https://chytbot.com#organization",
        name: "ChytBot",
        logo: {
          "@type": "ImageObject",
          url: "https://chytbot.com/favicon.png",
          width: 512,
          height: 512,
        },
      },
      inLanguage: "en-US",
      potentialAction: {
        "@type": "ReadAction",
        target: canonicalUrl,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": "https://chytbot.com#software",
      name: "ChytBot",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Customer Service Software",
      operatingSystem: "Web Browser",
      offers: {
        "@type": "AggregateOffer",
        lowPrice: "0",
        highPrice: "49",
        priceCurrency: "USD",
        offerCount: "3",
        availability: "https://schema.org/InStock",
      },
      description: "AI-powered chatbot builder for US businesses. Create intelligent customer support bots without coding.",
      // NOTE: aggregateRating removed — do not add back until real G2/Capterra reviews exist (v1.2+, FIX-01)
      featureList: [
        "No-code chatbot builder",
        "24/7 automated customer support",
        "Easy website integration",
        "Knowledge base training",
        "Multi-platform support",
      ],
      screenshot: image,
      softwareVersion: "2.0",
      countryOfOrigin: {
        "@type": "Country",
        name: "United States",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://chytbot.com#organization",
      name: "ChytBot",
      alternateName: "ChytBot Inc.",
      url: "https://chytbot.com",
      logo: "https://chytbot.com/favicon.png",
      description: "AI-powered chatbot platform helping American businesses automate customer support",
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      serviceType: "SaaS",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://chytbot.com#website",
      name: "ChytBot",
      url: "https://chytbot.com",
      description: "AI Chatbot Platform for US Businesses",
      publisher: {
        "@type": "Organization",
        "@id": "https://chytbot.com#organization",
        name: "ChytBot",
      },
      inLanguage: "en-US",
    },
  ];

  // Combine base and additional structured data
  const allStructuredData = additionalStructuredData 
    ? [...baseStructuredData, ...additionalStructuredData]
    : baseStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />

      {/* Geo-Targeting for US */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      <meta name="content-language" content="en-US" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang for US targeting */}
      <link rel="alternate" hrefLang="en-US" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="ChytBot" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Robots */}
      <meta 
        name="robots" 
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} 
      />
      <meta 
        name="googlebot" 
        content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} 
      />
      
      {/* Additional Meta Tags */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="3 days" />
      <meta name="author" content="ChytBot Inc." />
      <meta name="publisher" content="ChytBot Inc." />
      <meta name="coverage" content="United States" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="target" content="all" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(allStructuredData)}
      </script>

      {children}
    </Helmet>
  );
};
